import { Award, Factory, Sparkles, GraduationCap } from "lucide-react";

const generations = [
  {
    era: "1974",
    title: "The Foundation",
    name: "Late Shri Parasmal Kochar Mutha",
    role: "Founder & Visionary",
    description:
      "Hailing from Pali, Rajasthan — the heartland where all the goods are manufactured — and an ex-Indian Army personnel, Late Shri Parasmal Kochar Mutha was the first to introduce lining material to South India. He started from a small office space in Bangalore and travelled across the country as a trader and supplier of premium lining fabrics, laying the foundation for what would become South India's most trusted fabric house.",
    education: "B.Com, 1966 — Bangur College, Pali, Rajasthan",
    icon: Award,
    pastel: "pastel-peach",
    accent: "var(--mp-copper)",
  },
  {
    era: "1990s",
    title: "Manufacturing Expansion",
    name: "Mr. Naresh Kumar Mutha",
    role: "Second Generation",
    description:
      "Currently the Vice President of the Bangalore Wholesale Cloth Merchants Association, Mr. Naresh Kumar Mutha brings decades of expertise in manufacturing, wholesale, and distribution. He expanded operations from trading into large-scale manufacturing, established in-house production facilities, and enabled custom dyeing across thousands of colours — serving a rapidly growing client base across India.",
    education: "B.Com — Bangalore University",
    icon: Factory,
    pastel: "pastel-green",
    accent: "var(--mp-text)",
  },
  {
    era: "Present",
    title: "Modern Scale & Global Reach",
    name: "Mr. Rishab Mutha",
    role: "Third Generation",
    description:
      "Leading the company into its modern era with 9 premium quality lines, over 2,400 colours, and a ready stock of 15,00,000+ meters — with up to 8,000 m available per running shade at any given point of time. Combining heritage craftsmanship with global business acumen, he is taking the brand to clients across the globe — with the same commitment to quality established in 1974.",
    education: "BBM, Bangalore University · MBA, Hult International Business School, Boston, MA, USA",
    icon: Sparkles,
    pastel: "pastel-lavender",
    accent: "var(--mp-copper-deep)",
  },
];

export default function Timeline() {
  return (
    <section id="heritage" className="py-20 lg:py-32" style={{ backgroundColor: "var(--mp-paper)" }} data-testid="timeline-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Section Header */}
        <div className="text-center mb-16 lg:mb-24">
          <p
            className="text-xs uppercase tracking-[0.2em] font-semibold mb-4"
            style={{ color: "var(--mp-copper)", fontFamily: "'Manrope', sans-serif" }}
          >
            Three Generations of Excellence
          </p>
          <h1
            className="text-3xl md:text-4xl tracking-tight leading-tight font-medium mb-4"
            style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text)" }}
            data-testid="timeline-heading"
          >
            Our Heritage
          </h1>
          <div className="section-divider mx-auto" />
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Vertical line - desktop only */}
          <div className="hidden lg:block timeline-line" />

          <div className="space-y-16 lg:space-y-24">
            {generations.map((gen, idx) => {
              const Icon = gen.icon;
              const isEven = idx % 2 === 0;

              return (
                <div
                  key={gen.era}
                  className="relative animate-fade-in-up"
                  style={{ animationDelay: `${idx * 150}ms` }}
                  data-testid={`timeline-item-${idx}`}
                >
                  {/* Desktop layout */}
                  <div className={`hidden lg:grid lg:grid-cols-2 lg:gap-16 items-center`}>
                    {/* Left side */}
                    <div className={`${isEven ? 'text-right pr-16' : 'order-2 pl-16'}`}>
                      <span
                        className="text-5xl font-medium"
                        style={{ fontFamily: "'Cormorant Garamond', serif", color: gen.accent, opacity: 0.3 }}
                      >
                        {gen.era}
                      </span>
                    </div>

                    {/* Right side */}
                    <div className={`${isEven ? 'pl-16' : 'order-1 text-right pr-16'}`}>
                      <div
                        className={`${gen.pastel} p-8 border border-[var(--mp-line)] rounded-sm`}
                      >
                        <div className={`flex items-center gap-3 mb-4 ${isEven ? '' : 'justify-end'}`}>
                          <div
                            className="w-10 h-10 rounded-full flex items-center justify-center"
                            style={{ backgroundColor: `color-mix(in srgb, ${gen.accent} 14%, transparent)` }}
                          >
                            <Icon className="w-5 h-5" style={{ color: gen.accent }} />
                          </div>
                          <span
                            className="text-xs uppercase tracking-[0.15em] font-semibold"
                            style={{ color: gen.accent, fontFamily: "'Manrope', sans-serif" }}
                          >
                            {gen.title}
                          </span>
                        </div>
                        <h3
                          className="text-xl md:text-2xl tracking-tight font-medium mb-1"
                          style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text)" }}
                        >
                          {gen.name}
                        </h3>
                        <p
                          className="text-sm font-medium mb-3"
                          style={{ color: gen.accent, fontFamily: "'Manrope', sans-serif" }}
                        >
                          {gen.role}
                        </p>
                        <p
                          className="text-sm leading-relaxed"
                          style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}
                        >
                          {gen.description}
                        </p>
                        {gen.education && (
                          <div
                            className={`mt-4 pt-4 border-t border-[var(--mp-line)]/60 flex items-start gap-2 ${isEven ? '' : 'justify-end'}`}
                            data-testid={`timeline-education-${idx}`}
                          >
                            <GraduationCap className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: gen.accent }} />
                            <p
                              className="text-xs italic"
                              style={{ color: gen.accent, fontFamily: "'Manrope', sans-serif" }}
                            >
                              {gen.education}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Center dot - desktop */}
                  <div
                    className="hidden lg:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-4 rounded-full border-2"
                    style={{ borderColor: gen.accent, backgroundColor: "var(--mp-paper)" }}
                  />

                  {/* Mobile layout */}
                  <div className="lg:hidden">
                    <div className={`${gen.pastel} p-6 border border-[var(--mp-line)] rounded-sm`}>
                      <div className="flex items-center gap-3 mb-4">
                        <div
                          className="w-10 h-10 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: `color-mix(in srgb, ${gen.accent} 14%, transparent)` }}
                        >
                          <Icon className="w-5 h-5" style={{ color: gen.accent }} />
                        </div>
                        <div>
                          <span
                            className="text-xs uppercase tracking-[0.15em] font-semibold block"
                            style={{ color: gen.accent, fontFamily: "'Manrope', sans-serif" }}
                          >
                            {gen.title}
                          </span>
                          <span
                            className="text-2xl font-medium"
                            style={{ fontFamily: "'Cormorant Garamond', serif", color: gen.accent, opacity: 0.5 }}
                          >
                            {gen.era}
                          </span>
                        </div>
                      </div>
                      <h3
                        className="text-xl tracking-tight font-medium mb-1"
                        style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text)" }}
                      >
                        {gen.name}
                      </h3>
                      <p
                        className="text-sm font-medium mb-3"
                        style={{ color: gen.accent, fontFamily: "'Manrope', sans-serif" }}
                      >
                        {gen.role}
                      </p>
                      <p
                        className="text-sm leading-relaxed"
                        style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}
                      >
                        {gen.description}
                      </p>
                      {gen.education && (
                        <div
                          className="mt-4 pt-4 border-t border-[var(--mp-line)]/60 flex items-start gap-2"
                          data-testid={`timeline-education-mobile-${idx}`}
                        >
                          <GraduationCap className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: gen.accent }} />
                          <p
                            className="text-xs italic"
                            style={{ color: gen.accent, fontFamily: "'Manrope', sans-serif" }}
                          >
                            {gen.education}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
